/**
 * AvisoNaTela — parte D1 (01/10/2026), peça A do desenho.
 *
 * O aviso de notificação sai pelo Toaster do shadcn, não pelo do sonner: o agrupamento usa o
 * `update()` desse Toaster no MESMO aviso.
 * `mostrarAvisoNaTela(aviso, acoes)` é uma função comum (o avisador a chama). O conteúdo
 * é renderizado pelo Toaster, que mora DENTRO do Router e FORA do NotificationProvider:
 * por isso usa `useNavigate`, e o "marcar como lida" chega por `acoes`.
 *
 * Agrupamento: chegou outro antes de sumir → `update()` no MESMO toast ("N novos
 * avisos", corpo = o mais recente; "Lida" e "Abrir" marcam TODOS do grupo, o destino é o do
 * último — decisão do Augusto, 03/10/2026). ⚠️ Dois cuidados:
 *  - `TOAST_LIMIT = 1`: um `toast()` de outra tela expulsa o aviso, e `update()` num id
 *    expulso não faz nada — por isso `toastEstaAberto` antes de agrupar;
 *  - o Radix reinicia o relógio quando `duration` muda (alternamos 8000/8001, começando
 *    em 8000), MAS não limpa o relógio ao ir para `Infinity` (`startTimer` sai cedo):
 *    crítico que entra num grupo comum nasce num toast NOVO — senão fecharia em até 8 s.
 *
 * D2 (passo 14, 07/10/2026): o mesmo aviso leva também a MENSAGEM NOVA do chat (item com
 * `conversa`). Todos os itens do grupo da MESMA conversa → o `CartaoDaConversa` ("N mensagens
 * novas", "Silenciar"); senão vale o cartão do D1 acima. Mensagem não é notificação do sino:
 * o id dela (`message_id`) nunca vai para o `marcarLida`, e "Lida"/"Ver todas" só existem se
 * algum item do grupo é do sino.
 *
 * Passo 18, PR 2: um grupo com LEMBRETE também é permanente (D3-a: fica até Concluir, Adiar ou fechar) —
 * inclusive misturado com aviso comum; e um lembrete que chega num grupo comum nasce num toast NOVO pela
 * mesma armadilha do `Infinity`.
 *
 * D3 (passo 19): parametrizado por `ConfigDoAviso` (`criarAvisoNaTela`) para o portal; o item `atendimento` sai pelo
 * `CartaoDoAtendimento`.
 */
import { AlertCircle, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ToastAction, ToastClose, ToastDescription, ToastTitle } from "@/components/ui/toast";
import { toast, toastEstaAberto } from "@/hooks/use-toast";
import { caminhoSeguroInterno } from "@/lib/caminhoSeguroInterno";
import { cn } from "@/lib/utils";
import type { Lembrete } from "@/types/chat";
import { CartaoDaConversa } from "./CartaoDaConversa";
import { CartaoDoAtendimento } from "./CartaoDoAtendimento";
import { CartaoDoLembrete } from "./CartaoDoLembrete";
import { caminhoDaConversa, quandoDoAviso } from "./auxiliaresDoAviso";
import {
  apresentacaoDaCategoria,
  CATEGORIAS_DE_AVISO,
  type ApresentacaoDaCategoria,
} from "./categoriasDeAviso";

/** De que conversa (e de quem) é uma mensagem nova: o que o cartão da conversa precisa (D2). */
export interface ConversaDoAviso {
  room_id: string;
  room_type: string;
  /** O rótulo da conversa para quem recebe: o autor numa DM, o nome do grupo no resto. */
  sala: string;
  author_id: string;
  author_name: string;
  /** Cita a pessoa (ou o setor dela): o cartão ganha o `@` e a faixa âmbar. */
  mencao: boolean;
  /** A conversa já está silenciada (a menção que atravessa o silêncio): sem "Silenciar". */
  silenciada: boolean;
  /**
   * O id da linha de menção desta mensagem no sino (`chat.mention`/`chat.sector_mention`), quando
   * ela chegou. Nas conversas do chat a menção não anuncia — quem anuncia é o cartão da mensagem,
   * que não conhece a linha —, então o "Abrir" do cartão a marca como lida por aqui: o que a
   * pessoa viu não fica não lido no sino (decisão do Augusto, 03/10/2026).
   */
  idDoSino?: string;
}

/** Passo 18, PR 2: o lembrete por trás de um aviso `lembrete.tocou` — o cartão age por ele. */
export interface LembreteDoAviso {
  id: string;
  message_id: string;
  /** A sala de notas (a marca da bolha muda junto, se estiver em cache). */
  room_id: string | null;
}

/**
 * D3 (portal): de que resposta do atendente é o aviso. O item leva isto no lugar de `conversa`/`lembrete` e sai pelo
 * `CartaoDoAtendimento`. 🔴 NUNCA com `conversa`: o `CartaoDaConversa` chama a API interna (`useColegasInternos`) e, no portal, um
 * 401 interno apagaria o `seweb_auth` de uma sessão interna aberta no mesmo navegador (mapa A5).
 */
export interface AtendimentoDoAviso {
  session_id: string;
  /** Nome do setor, sem o código (`[FIN]`). */
  setor: string;
  /** Primeiro nome do atendente. */
  author_name: string;
}

export interface AvisoParaMostrar {
  /** Do sino: o id da notificação. De mensagem: o `message_id` (NUNCA vai para `marcarLida`). */
  id: string;
  title: string;
  body: string;
  severity: "info" | "warning" | "error" | "critical";
  categoria?: string | null;
  link?: string | null;
  created_at: string;
  /** Presente SÓ nos itens de mensagem do chat; os do sino não têm. */
  conversa?: ConversaDoAviso;
  /** Presente SÓ no aviso de lembrete que tocou. */
  lembrete?: LembreteDoAviso;
  /** Presente SÓ no aviso da resposta do atendente (portal). O `id` do item é o `message_id` e NUNCA vai para `marcarLida`. */
  atendimento?: AtendimentoDoAviso;
}

/** Um item de mensagem (com `conversa` garantida). */
export type ItemDeConversa = AvisoParaMostrar & { conversa: ConversaDoAviso };

/** Um item de lembrete (com `lembrete` garantido). */
export type ItemDeLembrete = AvisoParaMostrar & { lembrete: LembreteDoAviso };

/** Um item de resposta do atendente (com `atendimento` garantido) — D3, portal. */
export type ItemDeAtendimento = AvisoParaMostrar & { atendimento: AtendimentoDoAviso };

export interface AcoesDoAviso {
  marcarLida: (id: string) => void;
  /** Silenciar (ou reativar) os avisos de uma conversa; rejeita se o servidor falhar. */
  silenciarConversa?: (roomId: string, silenciar: boolean) => Promise<void>;
  /** Passo 18, PR 2: concluir ou adiar o lembrete do aviso; rejeita se o servidor recusar. */
  concluirLembrete?: (l: LembreteDoAviso) => Promise<Lembrete>;
  adiarLembrete?: (l: LembreteDoAviso) => Promise<Lembrete>;
}

type Gravidade = "info" | "warning" | "critico";
type Atualizar = ReturnType<typeof toast>["update"];

const DURACAO_MS = 8000;

/**
 * O que muda de um app para o outro (D3): quem valida o destino, para onde vai "Ver todas" e o corpo sem destino, e o mapa das
 * categorias (rodapé e ícone). O interno usa os valores de sempre; o portal passa os dele (`criarAvisoNaTela`).
 */
export interface ConfigDoAviso {
  caminhoSeguro: (link?: string | null) => string | null;
  inbox: string;
  categorias: Record<string, ApresentacaoDaCategoria>;
}

const CONFIG_INTERNA: ConfigDoAviso = {
  caminhoSeguro: caminhoSeguroInterno,
  inbox: "/notificacoes/inbox",
  categorias: CATEGORIAS_DE_AVISO,
};

const VARIANTE: Record<Gravidade, "default" | "warning" | "critico"> = {
  info: "default",
  warning: "warning",
  critico: "critico",
};
const FAIXA: Record<Gravidade, string> = {
  info: "border-l-4 border-l-primary",
  warning: "border-l-4 border-l-amber-500",
  critico: "",
};
const COR_DO_ICONE: Record<Gravidade, string> = {
  info: "bg-primary/10 text-primary",
  warning: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  critico: "bg-red-600/15 text-red-700 dark:text-red-300",
};

interface Grupo {
  id: string;
  itens: AvisoParaMostrar[];
  /** Fica até a pessoa agir: crítico, ou com lembrete no grupo (passo 18, PR 2). */
  permanente: boolean;
  update: Atualizar;
  dismiss: () => void;
}

let grupo: Grupo | null = null;

function gravidadeDe(itens: AvisoParaMostrar[]): Gravidade {
  if (itens.some((a) => a.severity === "critical" || a.severity === "error")) return "critico";
  if (itens.some((a) => a.severity === "warning")) return "warning";
  return "info";
}

/** Todos os itens são mensagens da MESMA conversa? Então vale o cartão da conversa. */
function todosDaMesmaConversa(itens: AvisoParaMostrar[]): itens is ItemDeConversa[] {
  const primeira = itens[0]?.conversa;
  return !!primeira && itens.every((a) => a.conversa?.room_id === primeira.room_id);
}

/** Todos os itens são lembretes que tocaram? Então vale o cartão do lembrete (passo 18, PR 2). */
function todosLembretes(itens: AvisoParaMostrar[]): itens is ItemDeLembrete[] {
  return itens.length > 0 && itens.every((a) => !!a.lembrete);
}

/** Todos os itens são respostas do atendente da MESMA sessão? Então vale o cartão do atendimento (D3). */
function todosDaMesmaSessao(itens: AvisoParaMostrar[]): itens is ItemDeAtendimento[] {
  const primeira = itens[0]?.atendimento;
  return !!primeira && itens.every((a) => a.atendimento?.session_id === primeira.session_id);
}

const temLembrete = (itens: AvisoParaMostrar[]) => itens.some((a) => !!a.lembrete);

interface ConteudoProps {
  itens: AvisoParaMostrar[];
  gravidade: Gravidade;
  acoes: AcoesDoAviso;
  aoFechar: () => void;
  config: ConfigDoAviso;
}

function ConteudoDoAviso({ itens, gravidade, acoes, aoFechar, config }: ConteudoProps) {
  const navigate = useNavigate();
  const atual = itens[itens.length - 1];
  const { rotuloCurto, Icone } = apresentacaoDaCategoria(atual.categoria, config.categorias);
  // Mensagem do chat sem link próprio leva à conversa dela.
  const destino =
    config.caminhoSeguro(atual.link) ??
    (atual.conversa ? caminhoDaConversa(atual.conversa.room_id) : null);

  // Decisão do Augusto (03/10/2026): no aviso agrupado ("N novos avisos"), "Lida" e "Abrir"
  // marcam TODOS os avisos do grupo — a pessoa já os viu, e os anteriores não podem ficar
  // não lidos no sino. Um id por vez (o `marcarLida` do contexto é por id), sem repetir o mesmo.
  // D2: só os itens do SINO (sem `conversa`) — o id de uma mensagem não é de notificação, e
  // um grupo só de mensagens não tem o que marcar (nem "Lida" nem "Ver todas").
  // D3: nem `atendimento` — o id da resposta do atendente também é de mensagem (portal).
  const idsDoSino = Array.from(
    new Set(itens.filter((a) => !a.conversa && !a.atendimento).map((a) => a.id))
  );
  const temSino = idsDoSino.length > 0;
  const marcarTodas = () => {
    for (const id of idsDoSino) acoes.marcarLida(id);
  };
  const abrir = () => {
    marcarTodas();
    navigate(destino ?? config.inbox); // o destino é o do último aviso do grupo
    aoFechar();
  };
  const lida = () => {
    marcarTodas();
    aoFechar();
  };
  const verTodas = () => {
    navigate(config.inbox);
    aoFechar();
  };

  return (
    <div className="group/aviso relative flex w-full gap-3 p-4 pr-10">
      {/* Corpo clicável = botão esticado ATRÁS do conteúdo (botão dentro de botão é HTML
          inválido); "Lida", "Ver todas" e o X ficam por cima. */}
      <button
        type="button"
        aria-label={`Abrir: ${atual.title}`}
        onClick={abrir}
        className="absolute inset-0 z-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full [&_svg]:h-4 [&_svg]:w-4",
          COR_DO_ICONE[gravidade]
        )}
      >
        <Icone />
      </span>
      <div className="pointer-events-none relative min-w-0 flex-1">
        {itens.length > 1 && (
          <p className="mb-2 border-b pb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {itens.length} novos avisos
          </p>
        )}
        <div className="flex min-w-0 items-center gap-2">
          {gravidade === "warning" && (
            <AlertTriangle
              aria-hidden="true"
              className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-300"
            />
          )}
          {gravidade === "critico" && (
            <AlertCircle
              aria-hidden="true"
              className="h-4 w-4 shrink-0 text-red-700 dark:text-red-300"
            />
          )}
          <ToastTitle className="truncate leading-5 group-hover/aviso:underline">
            {atual.title}
          </ToastTitle>
          {gravidade === "critico" && (
            <span className="ml-auto shrink-0 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white">
              Crítico
            </span>
          )}
        </div>
        {atual.body && (
          <ToastDescription className="line-clamp-2 leading-5 text-foreground/80">
            {atual.body}
          </ToastDescription>
        )}
        {/* Sem flex-wrap (cicatriz do flex-wrap no desktop): o rótulo trunca, as ações não encolhem. */}
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="min-w-0 truncate">
            {rotuloCurto} · {quandoDoAviso(atual.created_at)}
          </span>
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <div className="pointer-events-auto relative z-10 flex items-center gap-1">
              {temSino && itens.length > 1 && (
                <ToastAction
                  altText="Ver todas as notificações"
                  onClick={verTodas}
                  className="h-11 border-0 px-2 text-xs sm:h-8"
                >
                  Ver todas
                </ToastAction>
              )}
              {temSino && (
                <ToastAction
                  altText="Marcar como lida"
                  onClick={lida}
                  className="h-11 px-3 text-xs sm:h-8"
                >
                  Lida
                </ToastAction>
              )}
            </div>
            <span aria-hidden="true" className="text-xs font-medium text-primary">
              {destino ? "Abrir ›" : "Ver todas ›"}
            </span>
          </div>
        </div>
      </div>
      <ToastClose aria-label="Fechar aviso" className="z-10 opacity-100 sm:opacity-0" />
    </div>
  );
}

function propsDoToast(
  itens: AvisoParaMostrar[],
  acoes: AcoesDoAviso,
  aoFechar: () => void,
  config: ConfigDoAviso
) {
  // Duração: 8 s, alternando 8000/8001 (o 1º aviso dura exatamente 8000) para o Radix
  // reiniciar o relógio a cada aviso novo do grupo.
  const duracao = DURACAO_MS + ((itens.length - 1) % 2);

  if (todosLembretes(itens)) {
    // O cartão do lembrete: faixa âmbar ("pede atenção"), fica até a pessoa agir (D3-a), voz de aviso comum.
    return {
      variant: "default" as const,
      className: cn("p-0", FAIXA.warning),
      duration: Infinity,
      type: "background" as const,
      conteudo: <CartaoDoLembrete itens={itens} acoes={acoes} aoFechar={aoFechar} />,
    };
  }

  if (todosDaMesmaConversa(itens)) {
    // O cartão da conversa (D2): variante default; a menção do ÚLTIMO item pinta a faixa de
    // âmbar (sem virar "crítico"). Mensagem nunca é crítica, e a voz é a de um aviso comum.
    const mencao = itens[itens.length - 1].conversa.mencao;
    return {
      variant: "default" as const,
      className: cn("p-0", mencao ? FAIXA.warning : FAIXA.info),
      duration: duracao,
      type: "background" as const,
      conteudo: <CartaoDaConversa itens={itens} acoes={acoes} aoFechar={aoFechar} />,
    };
  }

  if (todosDaMesmaSessao(itens)) {
    // O cartão do atendimento (D3): sem foto, sem "Lida" (a resposta não é linha do sino), voz de aviso comum. Rajada da MESMA
    // sessão = "N respostas novas" no mesmo cartão; qualquer mistura cai no cartão genérico abaixo.
    const ultimo = itens[itens.length - 1];
    return {
      variant: "default" as const,
      className: cn("p-0", FAIXA.info),
      duration: duracao,
      type: "background" as const,
      conteudo: (
        <CartaoDoAtendimento
          itens={itens}
          destino={config.caminhoSeguro(ultimo.link)}
          alternativo={config.inbox}
          aoFechar={aoFechar}
        />
      ),
    };
  }

  const gravidade = gravidadeDe(itens);
  return {
    variant: VARIANTE[gravidade],
    className: cn("p-0", FAIXA[gravidade]),
    // Crítico/erro ficam até fechar, e o grupo que carrega um lembrete também (passo 18, PR 2). Os outros: 8 s
    // (ver `duracao` acima).
    duration: gravidade === "critico" || temLembrete(itens) ? Infinity : duracao,
    // `foreground` = aria-live assertive (o leitor de tela interrompe só para o crítico).
    type: gravidade === "critico" ? ("foreground" as const) : ("background" as const),
    conteudo: (
      <ConteudoDoAviso
        itens={itens}
        gravidade={gravidade}
        acoes={acoes}
        aoFechar={aoFechar}
        config={config}
      />
    ),
  };
}

function mostrar(aviso: AvisoParaMostrar, acoes: AcoesDoAviso, config: ConfigDoAviso): void {
  const vivo = grupo && toastEstaAberto(grupo.id) ? grupo : null;
  const itens = vivo ? [...vivo.itens, aviso] : [aviso];
  const permanente = gravidadeDe(itens) === "critico" || temLembrete(itens);
  // O conteúdo precisa do `dismiss` de um toast que ainda vai nascer.
  const alvo = { dismiss: () => {} };
  const props = propsDoToast(itens, acoes, () => alvo.dismiss(), config);

  if (vivo && !(permanente && !vivo.permanente)) {
    alvo.dismiss = vivo.dismiss;
    vivo.update({ ...props, id: vivo.id });
    grupo = { ...vivo, itens, permanente };
    return;
  }
  const novo = toast(props);
  alvo.dismiss = novo.dismiss;
  grupo = { id: novo.id, itens, permanente, update: novo.update, dismiss: novo.dismiss };
}

/** O aviso na tela do app interno: os valores de sempre. */
export function mostrarAvisoNaTela(aviso: AvisoParaMostrar, acoes: AcoesDoAviso): void {
  mostrar(aviso, acoes, CONFIG_INTERNA);
}

/**
 * D3 (passo 19): o aviso na tela com o destino, a inbox e as categorias de outro app (o portal). Devolve a função de mostrar já
 * configurada. O estado do grupo ("N novos avisos") é um só no módulo: interno e portal nunca coexistem na mesma página.
 */
export function criarAvisoNaTela(
  config: ConfigDoAviso
): (aviso: AvisoParaMostrar, acoes: AcoesDoAviso) => void {
  return (aviso, acoes) => mostrar(aviso, acoes, config);
}

/** Só para os testes. */
export function _reiniciarAvisoNaTelaParaTeste(): void {
  grupo = null;
}
